import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useState, useEffect } from 'react';
import Switch from './atoms/Switch';
import UserDTO, {
  UserRole, UpdateBusinessUserPermissionsDTO, displayUserPermissions, userPermissionsArray,
  UserBusiness,
} from '../models/user';
import { useUpdateUserBusinessMutation, useAddUserToEventMutation, useDeleteUserEventMutation } from '../store';
import { toastSuccess } from '../utils/toasts';
import Select from './atoms/Select/Select';
import Button from './atoms/Button';
import Input from './atoms/Input';
import Chip from './atoms/Chip';
import useGetSelectedBusiness from '../hooks/useGetSelectedBusiness';
import { EventUserPermissions } from '../models/invitation';

interface FormData {
  active: boolean;
  permissions: UserRole;
  notification_emails_enabled: boolean;
}

type ParsedUser = UserDTO & UserBusiness;

const validationSchema = yup.object().shape({
  active: yup.boolean().required(),
  permissions: yup.mixed<UserRole>().required(),
  notification_emails_enabled: yup.boolean().required(),
});

interface EditUserProps {
  user: ParsedUser | null;
  onSuccess: () => void;
}

const EditUser = ({ user, onSuccess }: EditUserProps) => {
  const [updateUserBusiness, { isLoading }] = useUpdateUserBusinessMutation();
  const [addUserToEvent] = useAddUserToEventMutation();
  const [deleteUserEvent] = useDeleteUserEventMutation();
  const { business } = useGetSelectedBusiness();
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);

  const form = useForm<FormData>({
    resolver: yupResolver<FormData>(validationSchema),
    defaultValues: {
      active: user?.active,
      permissions: user?.permissions,
      notification_emails_enabled: user?.notification_emails_enabled,
    },
  });

  const {
    handleSubmit, control, watch, setValue,
  } = form;

  // Initialize selected events from user's existing events for this business
  useEffect(() => {
    if (user?.user_events && business?.id) {
      const userEventIds = user.user_events
        .filter((ue) => {
          // Filter events that belong to the current business
          const event = business.events?.find((e) => e.id === ue.event_id);
          return event !== undefined;
        })
        .map((ue) => ue.event_id);
      setSelectedEvents(userEventIds);
    }
  }, [user, business]);

  const userBusinessToDto = (formData: FormData): UpdateBusinessUserPermissionsDTO => ({
    id: user?.id || '',
    active: formData.active,
    permissions: formData.permissions,
    notification_emails_enabled: formData.notification_emails_enabled,
  });

  const handleUpdateUserBusiness = async () => {
    try {
      const formData = form.getValues();
      const businessId = user?.business_id || '';

      // Update user business permissions
      const result = await updateUserBusiness({
        businessId,
        userBusiness: userBusinessToDto(formData),
      });

      if ('data' in result) {
        // Handle event changes for SELLER and COPRODUCER
        if ((formData.permissions === UserRole.SELLER || formData.permissions === UserRole.COPRODUCER) && business) {
          const currentUserEventIds = user?.user_events
            ?.filter((ue) => {
              const event = business.events?.find((e) => e.id === ue.event_id);
              return event !== undefined;
            })
            .map((ue) => ue.event_id) || [];

          // Find events to add
          const eventsToAdd = selectedEvents.filter((eventId) => !currentUserEventIds.includes(eventId));

          // Find events to remove
          const eventsToRemove = currentUserEventIds.filter((eventId) => !selectedEvents.includes(eventId));

          // Add new events
          // Get default commission rate from existing events if available
          const defaultCommissionRate = user?.user_events
            ?.find((ue) => {
              const event = business.events?.find((e) => e.id === ue.event_id);
              return event !== undefined;
            })?.seller_comission_rate || 0;

          const addPromises = eventsToAdd.map((eventId) => {
            const userEvent = user?.user_events?.find((ue) => ue.event_id === eventId);
            return addUserToEvent({
              businessId,
              eventId,
              userData: {
                userId: user?.user_id || user?.id || '',
                comissionRate: userEvent?.seller_comission_rate || defaultCommissionRate,
                active: userEvent?.active ?? true,
                permissions: formData.permissions === UserRole.COPRODUCER ? EventUserPermissions.COPRODUCER : EventUserPermissions.SELLER,
                seller_group_id: userEvent?.seller_group_id,
              },
            });
          });

          // Remove events
          const removePromises = eventsToRemove.map((eventId) => {
            const userEvent = user?.user_events?.find((ue) => ue.event_id === eventId);
            if (userEvent?.id) {
              return deleteUserEvent({
                businessId,
                eventId,
                userEventId: userEvent.id,
              });
            }
            return Promise.resolve();
          });

          await Promise.all([...addPromises, ...removePromises]);
        }

        toastSuccess('Usuario actualizado con éxito');
        onSuccess();
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleUpdateUserBusiness)}>
      <div className="flex flex-col gap-4">
        <Switch
          id="active"
          label="Estado del usuario"
          checked={watch('active')}
          onChange={(e) => setValue('active', e.target.checked)}
        />

        <Input
          label="Correo electrónico"
          id="email"
          value={user?.email}
          disabled
          labelAbove
        />

        <div>
          <Controller
            name="permissions"
            control={control}
            defaultValue={undefined}
            render={({ field }) => (
              <Select
                label="Nivel de permisos"
                id="permissions"
                options={userPermissionsArray.filter((p) => p !== 'USER').map((p) => ({
                  label: displayUserPermissions[p as keyof typeof displayUserPermissions],
                  value: p,
                }))}
                {...field}
              />
            )}
          />
        </div>

        {(form.watch('permissions') === 'ADMIN' || form.watch('permissions') === 'EDITOR') && (
          <Switch
            id="notification_emails_enabled"
            label="Notificaciones por email"
            checked={watch('notification_emails_enabled')}
            onChange={(e) => setValue('notification_emails_enabled', e.target.checked)}
          />
        )}

        {(form.watch('permissions') === UserRole.SELLER || form.watch('permissions') === UserRole.COPRODUCER) && (
          <div>
            <div>
              <Select
                label="Seleccionar evento"
                id="event-select"
                value=""
                onChange={(value) => {
                  const eventId = value.target.value;
                  if (!selectedEvents.includes(eventId)) {
                    const newSelectedEvents = [...selectedEvents, eventId];
                    setSelectedEvents(newSelectedEvents);
                  }
                }}
                options={business?.events
                  .filter((event) => !selectedEvents.includes(event.id))
                  .map((event) => ({
                    label: event.name,
                    value: `${event.id}`,
                  })) ?? []}
              />
              <div className="flex flex-wrap gap-2 mt-2">
                {business?.events
                  .filter((event) => selectedEvents.includes(event.id))
                  .map((event) => (
                    <Chip
                      key={event.id}
                      label={event.name}
                      onDelete={() => {
                        const newSelectedEvents = selectedEvents.filter((id) => id !== event.id);
                        setSelectedEvents(newSelectedEvents);
                      }}
                    />
                  ))}
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-row gap-4">
          <Button variant="secondary" onClick={() => onSuccess()}>Cancelar</Button>
          <Button variant="primary" type="submit" loading={isLoading}>Guardar</Button>
        </div>
      </div>
    </form>
  );
};

export default EditUser;
